import { useCallback, useState } from "react";
import { toast } from "sonner";
import { hideImpact } from "@/lib/formUsage";
import { TABS } from "@/components/booking-form/registry/tabs";
import { FIELDS } from "@/components/booking-form/registry/fields";
import { SLOTS } from "@/components/booking-form/registry/slots";

const warnHideImpact = (scope, key) => {
  const lines = hideImpact(scope, key, { fields: FIELDS, slots: SLOTS });
  if (!lines.length) return;
  const list = scope === "tab" ? TABS : scope === "slot" ? SLOTS : FIELDS;
  const name = list.find((x) => x.key === key)?.label || key;
  toast.warning(`Hiding "${name}" also hides it in:`, {
    description: <ul className="mt-1 space-y-0.5 text-xs" data-testid="fd-hide-impact">{lines.map((l) => <li key={l}>• {l}</li>)}</ul>,
    duration: 8000,
  });
};

// Unsaved edits layered over saved override rows over registry defaults.
export function useDraftOverrides(overrides) {
  const [draft, setDraftState] = useState({});
  const setDraft = useCallback((v) => setDraftState(v), []);

  const savedFor = (scope, key) =>
    overrides.find((o) => o.scope === scope && o.field_key === key) || null;

  const effective = (scope, key, prop, fallback) => {
    const d = draft[`${scope}:${key}`];
    if (d && d[prop] !== undefined) return d[prop];
    const s = savedFor(scope, key);
    if (s && s[prop] !== null && s[prop] !== undefined) return s[prop];
    return fallback;
  };

  const setDraftValue = (scope, key, prop, val) => {
    if (prop === "is_visible" && val === false) warnHideImpact(scope, key);
    const dk = `${scope}:${key}`;
    setDraftState((d) => ({ ...d, [dk]: { ...(d[dk] || {}), [prop]: val } }));
  };

  const discardDraft = (scope, key) => {
    const dk = `${scope}:${key}`;
    setDraftState((d) => { const n = { ...d }; delete n[dk]; return n; });
  };

  const isDirty = (scope, key) => {
    const d = draft[`${scope}:${key}`];
    return d && Object.keys(d).length > 0;
  };

  const dirtyCount = Object.keys(draft).filter((k) => draft[k] && Object.keys(draft[k]).length).length;

  return { draft, setDraft, savedFor, effective, setDraftValue, discardDraft, isDirty, dirtyCount };
}
